/**
 * Turns a failed workflow callable rejection into one sentence an operator can
 * act on. Called only through `workflowRunErrorOutcome`, which every workflow run
 * site reports through; it used to sit under `ArTable/components`, where three of
 * the five sites could not reach it.
 *
 * The code arrives prefixed — `FunctionsError` sets `functions/<code>` — so this
 * function reads it through `readCallableError`. Compared raw, its `internal` and
 * `deadline-exceeded` branches never matched and the callable's flattened
 * `INTERNAL` reached the screen verbatim (SYD-2445; SYD-2325 shipped dead).
 *
 * `details.rawMessage` is logged with `details.invocationId` and never displayed:
 * it is an unhandled throw's own text, which names server internals and tells the
 * operator nothing. The invocation id in the sentence is what support joins back
 * to that log line and to the trace.
 *
 * The server's own message always wins. A run marker names a phase, which the
 * sidebar banner already shows as its own row, so it enters the prose only when
 * there is no message to show — testing the marker first replaced
 * `Missing sourceTableId for updateCells action` with a generic sentence, and
 * turned a proven-safe-to-retry refusal into "nothing is confirmed" (SYD-2445).
 */

import { readCallableError } from './readCallableError';
import { workflowRunStageLabel } from './workflowRunStageLabel';
import { workflowActionTypeLabel } from '../workflowActionTypeLabel';
import type { CallableErrorDetails } from '#types';

const actionClause = (details: CallableErrorDetails): string =>
  details.actionType ? ` on the ${workflowActionTypeLabel(details.actionType)} action` : '';

const referenceClause = (details: CallableErrorDetails | undefined): string =>
  details?.invocationId ? ` Quote reference ${details.invocationId} to support.` : '';

/**
 * Whether the callable flattened its error into nothing an operator can read: an
 * empty message, or the status code's own name. `FAILED_PRECONDITION` for
 * `failed-precondition` is that name in gRPC's spelling, so the underscores are
 * normalized before the comparison. A rejection that carries no code at all is a
 * local throw, whose message is its own.
 */
const isContentless = (code: string | undefined, message: string): boolean =>
  !!code && (message === '' || message.toLowerCase().replace(/_/g, '-') === code);

/**
 * The server's sentence with a full stop, so the reference clause after it does not
 * run into it. Applied only when something follows: a message shown on its own
 * reaches the operator exactly as the server wrote it.
 */
const terminated = (message: string): string => (/[.!?]$/.test(message) ? message : `${message}.`);

const formatWorkflowErrorMessage = (
  error: unknown,
  workflowLabel: string | undefined,
): string => {
  const { code, message, details } = readCallableError(error);
  const label = workflowLabel ?? 'The workflow';

  if (details?.rawMessage) {
    console.error('[workflow] run failed on the server', {
      invocationId: details.invocationId,
      functionName: details.functionName,
      errorKind: details.errorKind,
      rawMessage: details.rawMessage,
    });
  }

  const reference = referenceClause(details);

  if (code === 'deadline-exceeded') {
    return 'The Signify service did not respond in time. Your request may still be processing'
      + ` — check the communication log and Signify before retrying.${reference}`;
  }

  if (isContentless(code, message)) {
    const stopped = details?.stage
      ? ` at the ${workflowRunStageLabel(details.stage)} stage${actionClause(details)}`
      : ' before reporting a reason';
    return `${label} failed${stopped}.`
      + ` Nothing is confirmed either way — check the communication log before retrying.${reference}`;
  }

  if (message) return reference ? `${terminated(message)}${reference}` : message;

  return `${workflowLabel ?? 'Workflow'} failed. Please try again.`;
};

export default formatWorkflowErrorMessage;
